import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import { cloneDeep, get, merge, set } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    attachChildren,
    getActivityQuestionsById,
    getAssessmentQuestionsById,
    saveResponse,
} from 'App/services/AMSService';
import { isoDateOnlyFormat } from 'App/services/DateService';
import { SynchronousQueue } from 'App/utils/synchronousQueue';
import { patientPropType } from 'Shared/types';
import { viewModes } from '../constants';
import { activityPropType, questionPropType } from '../types';
import convertAssessmentQuestionToView from '../utils/convertAssessmentQuestionToView';
import getResponseIds from '../utils/getResponseIds';
import AssessmentQuestionBaseField from './AssessmentQuestionBaseField';
import AssessmentQuestionChildren from './AssessmentQuestionChildren';
import ConfirmDeleteChildrenModal from './ConfirmDeleteChildrenModal';

const responseSelectQueue = new SynchronousQueue();

const AssessmentQuestionStyle = styled.div`
    margin-bottom: 25px;

    .form-check {
        margin-left: 10px;
    }

    .form-control,
    .react-datepicker-wrapper {
        width: 300px;
    }

    textarea.form-control {
        width: 100%;
    }

    .required-asterisk {
        color: var(--brandDanger);
        font-size: 22px;
        line-height: 1px;
        vertical-align: middle;
    }
`;

const AssessmentQuestion = ({ question, activity, patient, isSpecialQuestion }) => {
    const { values, setValues, setSubmitting } = useFormikContext();
    const valuesRef = useRef(values);
    useEffect(() => {
        valuesRef.current = values;
    }, [values]);

    const questionValues = values[question.questionId];
    const getChildrenOfQuestionId = useCallback(
        (questionId) => {
            return Object.values(values).filter((q) => q.questionId && q.parentQuestionId === questionId);
        },
        [values]
    );
    const questionChildrenList = useMemo(
        () => getChildrenOfQuestionId(question.questionId),
        [getChildrenOfQuestionId, question.questionId]
    );
    const [showDeleteChildrenModal, setShowDeleteChildrenModal] = useState(false);
    const [pendingValue, setPendingValue] = useState(null);

    const getAllChildIds = useCallback(
        (childrenList, newValue) => {
            if (!childrenList) return [];
            const responseIds = getResponseIds(newValue);
            const responseIdsArray = responseIds == null ? [] : [].concat(responseIds);
            return childrenList.reduce((idsAgg, currQuestion) => {
                const isChildStillPresent = responseIdsArray.includes(currQuestion.parentAnswerId);
                if (!isChildStillPresent) {
                    idsAgg.push(currQuestion.questionId);
                    const currChildren = getChildrenOfQuestionId(currQuestion.questionId);
                    if (currChildren) {
                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line react-hooks/immutability
                        idsAgg.push(...getAllChildIds(currChildren, newValue));
                    }
                }
                return idsAgg;
            }, []);
        },
        [getChildrenOfQuestionId]
    );

    const getNumberOfAnswers = useCallback(
        (newValue) => {
            const childIds = getAllChildIds(questionChildrenList, newValue);
            return childIds.reduce((agg, currId) => {
                const value = get(valuesRef.current, `${currId}.response`);
                if (value) {
                    return agg + (Array.isArray(value) ? value.length : 1);
                }
                return agg;
            }, 0);
        },
        [getAllChildIds, questionChildrenList]
    );

    const getQuestionsByIds = useCallback(
        async (ids) => {
            if (!ids.length || (ids.length === 1 && ids[0] === undefined)) return [];
            if (!activity.id) {
                return getAssessmentQuestionsById(activity.assessment.id, ids);
            }
            const newQuestions = await getActivityQuestionsById(activity.id, ids);
            return newQuestions;
        },
        [activity.assessment.id, activity.id]
    );

    const commitSaveResponse = useCallback(
        async ({
            value,
            activityDateQuestion,
            activityDateValue,
            needByDateQuestion,
            needByDateValue,
            removedChildIds,
        }) => {
            if (!activity.id) {
                return;
            }

            setSubmitting(true);
            try {
                const valueToSave = getResponseIds(value) ?? '';
                await saveResponse(activity.id, question.questionId, valueToSave);
                if (removedChildIds?.length) {
                    await Promise.all(
                        removedChildIds.map((childId) => {
                            const childQuestionType = get(valuesRef.current, `${childId}.type`);
                            const emptyResponse = ['checkbox', 'multiselect'].includes(childQuestionType) ? [] : '';
                            return saveResponse(activity.id, childId, emptyResponse);
                        })
                    );
                }
                if (activityDateQuestion) {
                    await saveResponse(activity.id, activityDateQuestion.questionId, activityDateValue);
                }
                if (needByDateQuestion) {
                    await saveResponse(activity.id, needByDateQuestion.questionId, needByDateValue);
                }
            } finally {
                setSubmitting(false);
            }
        },
        [activity.id, question.questionId, setSubmitting]
    );

    const setNewChildrenValues = useCallback(
        async (value) => {
            const answersArray = Object.values(question.answers);
            if (!answersArray.length) return;
            const responseIds = getResponseIds(value);
            const responseIdsArray = responseIds == null ? [] : [].concat(responseIds);
            const fullAnswerValues = answersArray.filter((a) => responseIdsArray.includes(a.id));
            const allChildIds = fullAnswerValues.map((val) => val?.children).flat();
            const newChildren = await getQuestionsByIds(allChildIds);

            const newChildrenList = attachChildren(newChildren).map(convertAssessmentQuestionToView);
            const valueChanges = newChildrenList.reduce(
                (agg, child) => merge(agg, { [child.questionId]: child }),
                cloneDeep(valuesRef.current)
            );
            set(
                valueChanges,
                `${question.questionId}.children`,
                newChildrenList.filter((child) =>
                    [question.questionId, question.officialQuestionId].includes(child.parentQuestionId)
                )
            );
            setValues(valueChanges);
        },
        [getQuestionsByIds, question.answers, question.officialQuestionId, question.questionId, setValues]
    );

    const commitChange = useCallback(
        async (value) => {
            const childIds = getAllChildIds(questionChildrenList, value);
            const valueChanges = childIds.reduce(
                (agg, currId) => set(agg, `${currId}.response`, undefined),
                valuesRef.current
            );

            let finalValue = value;

            if (question.type === 'date' && value) {
                const utcDate = dayjs.utc(value).format(isoDateOnlyFormat);
                finalValue = dayjs(utcDate, isoDateOnlyFormat).toDate();
            }

            set(valueChanges, `${question.questionId}.response`, finalValue);
            set(valueChanges, `${question.questionId}.mobileUserResponse`, false);

            // Update values for Activity Date & Need By Date
            const valuesList = Object.values(valuesRef.current);
            const activityDateQuestion =
                question.name !== 'activity_date' &&
                valuesList.find((questionValue) => questionValue.name === 'activity_date' && !questionValue.response);
            const activityDateValue = dayjs().toDate();
            if (activityDateQuestion) {
                set(valueChanges, `${activityDateQuestion.questionId}.response`, activityDateValue);
            }
            const needByDateQuestion =
                question.name !== 'need_by_date' &&
                valuesList.find((questionValue) => questionValue.name === 'need_by_date' && !questionValue.response);
            const needByDateValue = dayjs((question.name === 'activity_date' && value) || undefined)
                .add(7, 'days')
                .toDate();
            if (needByDateQuestion) {
                set(valueChanges, `${needByDateQuestion.questionId}.response`, needByDateValue);
            }

            setValues(valueChanges);

            setNewChildrenValues(value);

            responseSelectQueue.enqueue(() =>
                commitSaveResponse({
                    value,
                    activityDateQuestion,
                    activityDateValue,
                    needByDateQuestion,
                    needByDateValue,
                    removedChildIds: childIds,
                })
            );
        },
        [
            commitSaveResponse,
            getAllChildIds,
            question.name,
            question.questionId,
            questionChildrenList,
            setNewChildrenValues,
            setValues,
            question.type,
        ]
    );

    const handleChange = useCallback(
        (event) => {
            let value = event?.target?.value ?? event;
            if (value && value instanceof Date) {
                value = dayjs(value).startOf('day').utc().toISOString();
            }
            const numberOfAnswers = getNumberOfAnswers(value);
            if (numberOfAnswers > 0) {
                setPendingValue(value);
                setShowDeleteChildrenModal(true);
            } else {
                commitChange(value);
            }
        },
        [commitChange, getNumberOfAnswers]
    );

    const handleCloseDeleteChildrenModal = (confirm) => {
        if (confirm) {
            commitChange(pendingValue);
        }
        setShowDeleteChildrenModal(false);
    };

    const renderChildrenQuestions = (answerId) => {
        return (
            <AssessmentQuestionChildren
                questionValues={questionValues}
                activity={activity}
                patient={patient}
                answerId={answerId}
            />
        );
    };

    if (question.attributes.hidden && (!question.response || question.response === 0)) {
        return null;
    }

    /**
     * ViewMode should always be set.
     * Render nothing if the view mode is mobile and this is not a mobile question.
     */
    if (!isSpecialQuestion && values.viewMode.label === viewModes.VIEW_MOBILE && !question.attributes.is_mobile) {
        return null;
    }

    return (
        <AssessmentQuestionStyle id={question.questionId}>
            <AssessmentQuestionBaseField
                question={question}
                questionValues={questionValues}
                activity={activity}
                patient={patient}
                viewMode={values.viewMode}
                onChange={handleChange}
            />
            {question.type !== 'checkbox' && renderChildrenQuestions()}
            {showDeleteChildrenModal && (
                <ConfirmDeleteChildrenModal
                    numberOfAnswers={getNumberOfAnswers(pendingValue)}
                    onClose={handleCloseDeleteChildrenModal}
                />
            )}
        </AssessmentQuestionStyle>
    );
};

AssessmentQuestion.propTypes = {
    question: questionPropType.isRequired,
    activity: activityPropType.isRequired,
    patient: patientPropType.isRequired,
    isSpecialQuestion: PropTypes.bool,
};

AssessmentQuestion.defaultProps = {
    isSpecialQuestion: false,
};

export default AssessmentQuestion;
